import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faPenToSquare, faTrash, faPlus, faExpand } from '@fortawesome/free-solid-svg-icons';
import Modal from './Modal';
import EventIntakeFill from './EventIntakeFill';
import {
  createIntakeField,
  deleteIntakeField,
  fetchIntakeForm,
  fetchIntakeResponses,
  updateIntakeField,
  updateIntakeForm,
  INTAKE_FIELD_TYPE_LABELS,
  type IntakeField,
  type IntakeFieldType,
  type IntakeForm,
  type IntakeResponse,
} from '../api/events';
import './EventIntakeFormTab.css';

const FIELD_TYPES: IntakeFieldType[] = ['text', 'email', 'textarea', 'number', 'phone', 'date', 'select'];

type SubTab = 'builder' | 'fill' | 'responses';

interface Props {
  eventId: number;
  viewerUserId: number;
  canEdit: boolean;
  isVoid: boolean;
}

function escapeCsvCell(value: string): string {
  if (/[",\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

function formatSubmittedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const EventIntakeFormTab: React.FC<Props> = ({ eventId, viewerUserId, canEdit, isVoid }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState<IntakeForm | null>(null);
  const [fields, setFields] = useState<IntakeField[]>([]);
  const [responses, setResponses] = useState<IntakeResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [subTab, setSubTab] = useState<SubTab>('fill');

  // Form meta editing
  const [titleDraft, setTitleDraft] = useState('');
  const [descDraft, setDescDraft] = useState('');
  const [metaSaving, setMetaSaving] = useState(false);

  // Field editor modal
  const [fieldModalOpen, setFieldModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<IntakeField | null>(null);
  const [fLabel, setFLabel] = useState('');
  const [fType, setFType] = useState<IntakeFieldType>('text');
  const [fRequired, setFRequired] = useState(false);
  const [fOptions, setFOptions] = useState('');
  const [fieldSaving, setFieldSaving] = useState(false);
  const [fieldError, setFieldError] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [{ form: f, fields: flds }, resp] = await Promise.all([
        fetchIntakeForm(eventId, viewerUserId),
        fetchIntakeResponses(eventId, viewerUserId),
      ]);
      setForm(f);
      setFields(flds);
      setResponses(resp);
      setTitleDraft(f.title);
      setDescDraft(f.description ?? '');
      setSubTab(flds.length === 0 && canEdit ? 'builder' : 'fill');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load intake form.');
    } finally {
      setLoading(false);
    }
  }, [eventId, viewerUserId, canEdit]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const saveMeta = async () => {
    setMetaSaving(true);
    try {
      const updated = await updateIntakeForm(eventId, {
        user_id: viewerUserId,
        title: titleDraft.trim() || 'Event intake form',
        description: descDraft.trim() || null,
      });
      setForm(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save form.');
    } finally {
      setMetaSaving(false);
    }
  };

  const openAddField = () => {
    setEditingField(null);
    setFLabel('');
    setFType('text');
    setFRequired(false);
    setFOptions('');
    setFieldError('');
    setFieldModalOpen(true);
  };

  const openEditField = (field: IntakeField) => {
    setEditingField(field);
    setFLabel(field.label);
    setFType(field.field_type);
    setFRequired(field.required);
    setFOptions((field.options ?? []).join('\n'));
    setFieldError('');
    setFieldModalOpen(true);
  };

  const saveField = async () => {
    const label = fLabel.trim();
    if (!label) {
      setFieldError('Field label is required.');
      return;
    }
    const options =
      fType === 'select'
        ? fOptions
            .split('\n')
            .map((o) => o.trim())
            .filter((o) => o !== '')
        : [];
    if (fType === 'select' && options.length === 0) {
      setFieldError('Add at least one dropdown option (one per line).');
      return;
    }
    setFieldSaving(true);
    setFieldError('');
    try {
      if (editingField) {
        const updated = await updateIntakeField(eventId, editingField.id, {
          user_id: viewerUserId,
          label,
          field_type: fType,
          required: fRequired,
          options,
        });
        setFields((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
      } else {
        const created = await createIntakeField(eventId, {
          user_id: viewerUserId,
          label,
          field_type: fType,
          required: fRequired,
          options,
        });
        setFields((prev) => [...prev, created]);
      }
      setFieldModalOpen(false);
    } catch (e) {
      setFieldError(e instanceof Error ? e.message : 'Failed to save field.');
    } finally {
      setFieldSaving(false);
    }
  };

  const removeField = async (field: IntakeField) => {
    if (!window.confirm(`Delete the "${field.label}" field? Existing responses keep their saved values.`)) return;
    try {
      await deleteIntakeField(eventId, field.id, viewerUserId);
      setFields((prev) => prev.filter((f) => f.id !== field.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete field.');
    }
  };

  const downloadCsv = () => {
    if (fields.length === 0) return;
    const headers = [...fields.map((f) => f.label), 'Submitted at'];
    const body = responses.map((r) => [
      ...fields.map((f) => r.answers[String(f.id)] ?? ''),
      r.created_at,
    ]);
    const csv = [headers, ...body]
      .map((line) => line.map((c) => escapeCsvCell(String(c))).join(','))
      .join('\r\n');
    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safe = (form?.title || 'event-intake').replace(/[^\w\-]+/g, '_').slice(0, 60);
    a.href = url;
    a.download = `${safe}_responses.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const subTabs = useMemo(() => {
    const list: { id: SubTab; label: string }[] = [];
    if (canEdit) list.push({ id: 'builder', label: 'Builder' });
    list.push({ id: 'fill', label: 'Fill out' });
    list.push({ id: 'responses', label: `Responses (${responses.length})` });
    return list;
  }, [canEdit, responses.length]);

  if (loading) return <p className="page-subtitle">Loading intake form…</p>;
  if (error) return <p className="create-order-error" role="alert">{error}</p>;

  return (
    <div className="intake-tab">
      <div className="intake-subtabs" role="tablist" aria-label="Intake form sections">
        {subTabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={subTab === t.id}
            className={`intake-subtab${subTab === t.id ? ' intake-subtab--active' : ''}`}
            onClick={() => setSubTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'builder' && canEdit ? (
        <div className="intake-builder">
          <div className="intake-builder-meta">
            <div className="form-group">
              <label htmlFor="intake-title" className="form-label">
                Form title
              </label>
              <input
                id="intake-title"
                type="text"
                className="form-input"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                disabled={metaSaving}
              />
            </div>
            <div className="form-group">
              <label htmlFor="intake-desc" className="form-label">
                Description <span className="intake-optional">(optional)</span>
              </label>
              <textarea
                id="intake-desc"
                className="form-input"
                rows={2}
                value={descDraft}
                onChange={(e) => setDescDraft(e.target.value)}
                disabled={metaSaving}
                placeholder="Shown above the form when people fill it out."
              />
            </div>
            <button type="button" className="update-button" onClick={() => void saveMeta()} disabled={metaSaving}>
              {metaSaving ? 'Saving…' : 'Save form details'}
            </button>
          </div>

          <div className="intake-fields-header">
            <h3 className="intake-section-title">Fields</h3>
            <button type="button" className="page-toolbar-btn" onClick={openAddField}>
              <FontAwesomeIcon icon={faPlus} /> Add field
            </button>
          </div>

          {fields.length === 0 ? (
            <p className="page-subtitle">No fields yet. Add a field to start building the form.</p>
          ) : (
            <ul className="intake-field-list">
              {fields.map((field) => (
                <li key={field.id} className="intake-field-row">
                  <div className="intake-field-info">
                    <span className="intake-field-label">{field.label}</span>
                    <span className="intake-field-meta">
                      {INTAKE_FIELD_TYPE_LABELS[field.field_type]}
                      {field.required ? ' · required' : ''}
                      {field.field_type === 'select' && field.options.length > 0
                        ? ` · ${field.options.length} option${field.options.length === 1 ? '' : 's'}`
                        : ''}
                    </span>
                  </div>
                  <div className="intake-field-actions">
                    <button type="button" className="intake-icon-btn" onClick={() => openEditField(field)} aria-label="Edit field">
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </button>
                    <button
                      type="button"
                      className="intake-icon-btn intake-icon-btn--danger"
                      onClick={() => void removeField(field)}
                      aria-label="Delete field"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {subTab === 'fill' ? (
        <div>
          {fields.length > 0 && !isVoid ? (
            <div className="intake-fill-toolbar">
              <button
                type="button"
                className="page-toolbar-btn"
                onClick={() => navigate(`/events/${eventId}/intake`)}
              >
                <FontAwesomeIcon icon={faExpand} /> Full screen
              </button>
            </div>
          ) : null}
          <EventIntakeFill
            eventId={eventId}
            form={form}
            fields={fields}
            isVoid={isVoid}
            canEdit={canEdit}
            onSubmitted={(created) => setResponses((prev) => [created, ...prev])}
          />
        </div>
      ) : null}

      {subTab === 'responses' ? (
        <div className="intake-responses">
          <div className="intake-responses-header">
            <h3 className="intake-section-title">Responses ({responses.length})</h3>
            <button
              type="button"
              className="page-toolbar-btn"
              onClick={downloadCsv}
              disabled={fields.length === 0 || responses.length === 0}
            >
              <FontAwesomeIcon icon={faDownload} /> Download CSV
            </button>
          </div>
          {fields.length === 0 ? (
            <p className="page-subtitle">Add fields to the form to start collecting responses.</p>
          ) : (
            <div className="intake-responses-table-wrap">
              <table className="tasks-search-results-table">
                <thead>
                  <tr>
                    {fields.map((f) => (
                      <th key={f.id}>{f.label}</th>
                    ))}
                    <th>Submitted at</th>
                  </tr>
                </thead>
                <tbody>
                  {responses.length === 0 ? (
                    <tr>
                      <td colSpan={fields.length + 1} className="intake-empty-cell">
                        No responses yet.
                      </td>
                    </tr>
                  ) : (
                    responses.map((r) => (
                      <tr key={r.id}>
                        {fields.map((f) => (
                          <td key={f.id} data-label={f.label}>
                            {r.answers[String(f.id)] || '—'}
                          </td>
                        ))}
                        <td data-label="Submitted at">{formatSubmittedAt(r.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      <Modal isOpen={fieldModalOpen} onClose={() => setFieldModalOpen(false)} title={editingField ? 'Edit field' : 'Add field'}>
        <div className="modal-body">
          <div className="form-group">
            <label htmlFor="intake-field-label" className="form-label">
              Field label
            </label>
            <input
              id="intake-field-label"
              type="text"
              className="form-input"
              value={fLabel}
              onChange={(e) => {
                setFLabel(e.target.value);
                setFieldError('');
              }}
              placeholder="e.g. Full name"
              disabled={fieldSaving}
            />
          </div>
          <div className="form-group">
            <label htmlFor="intake-field-type" className="form-label">
              Field type
            </label>
            <select
              id="intake-field-type"
              className="form-input"
              value={fType}
              onChange={(e) => setFType(e.target.value as IntakeFieldType)}
              disabled={fieldSaving}
            >
              {FIELD_TYPES.map((t) => (
                <option key={t} value={t}>
                  {INTAKE_FIELD_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          {fType === 'select' ? (
            <div className="form-group">
              <label htmlFor="intake-field-options" className="form-label">
                Dropdown options (one per line)
              </label>
              <textarea
                id="intake-field-options"
                className="form-input"
                rows={4}
                value={fOptions}
                onChange={(e) => setFOptions(e.target.value)}
                placeholder={'Option A\nOption B\nOption C'}
                disabled={fieldSaving}
              />
            </div>
          ) : null}
          <div className="form-group intake-required-row">
            <label className="intake-required-check">
              <input
                type="checkbox"
                checked={fRequired}
                onChange={(e) => setFRequired(e.target.checked)}
                disabled={fieldSaving}
              />
              Required field
            </label>
          </div>
          {fieldError ? <p className="create-order-error" role="alert">{fieldError}</p> : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={fieldSaving} onClick={() => setFieldModalOpen(false)}>
            Cancel
          </button>
          <button type="button" className="update-button" disabled={fieldSaving} onClick={() => void saveField()}>
            {fieldSaving ? 'Saving…' : editingField ? 'Save field' : 'Add field'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default EventIntakeFormTab;
